import { useState, useEffect, useMemo, useCallback, useRef, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Trash2, AlertTriangle, ArrowLeft, Truck, X } from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import SearchableSelect, {
  type SearchableSelectOption,
} from '../../components/SearchableSelect';
import AsyncItemSelect from '../../components/AsyncItemSelect';
import ItemFormModal from '../../components/ItemFormModal';
import {
  createPurchaseOrder,
  updatePurchaseOrder,
  getPurchaseOrderById,
} from '../../utils/purchase/purchaseOrderApi';
import { getVendorPayableBills } from '../../utils/billApi';
import { getBillPayments } from '../../utils/billPaymentApi';
import { getSuppliers, getSupplierById, createSupplier } from '../../utils/supplierApi';
import { ApiValidationError } from '../../types/auth';
import DecimalInput from '../../components/DecimalInput';
import { btnPrimary } from '../../utils/uiTheme';
import { blurActiveInputBeforeSave } from '../../utils/numberInput';
import { displayRate } from '../../utils/linePricing';
import { formatCurrency, formatPreviewQuantity } from '../../utils/formatCurrency';
import { toast, toastApiError } from '../../utils/toast';
import {
  emptyCustomerSnapshot,
  snapshotToPayload,
  validateCustomerSnapshot,
} from '../../utils/customerSnapshot';
import type { CustomerSnapshot } from '../../types/invoice';
import {
  getPurchaseOrderVendorName,
  PURCHASE_ORDER_STATUSES,
  type CreatePurchaseOrderData,
  type PurchaseOrder,
  type PurchaseOrderLine,
  type PurchaseOrderStatus,
  type UpdatePurchaseOrderData,
} from '../../types/purchase/purchaseOrder';
import type { Supplier, CreateSupplierData } from '../../types/supplier';
import type { Item } from '../../types/item';
import { qtyOnHandClassName } from '../../utils/negativeInventory';
import { todayLocalIso } from '../../utils/localDate';
import { scrollIntoViewAfterPaint } from '../../utils/scrollIntoViewAfterPaint';

const formatLabel = (value: string) =>
  value
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

const supplierToOption = (supplier: Supplier): SearchableSelectOption => ({
  value: supplier.id,
  label: supplier.display_name,
  description:
    [supplier.email, supplier.phone].filter(Boolean).join(' · ') || undefined,
});

const supplierToSnapshot = (supplier: Supplier): CustomerSnapshot => ({
  name: supplier.display_name,
  email: supplier.email,
  phone: supplier.phone,
  address: supplier.address,
});

const canManagePurchaseOrders = () => {
  const role = localStorage.getItem('user_role')?.toUpperCase();
  return role === 'ADMIN' || role === 'ACCOUNTANT';
};

const round2 = (n: number) => Math.round(n * 100) / 100;

interface LocalLine {
  item_id: number;
  item_name: string;
  item_sku: string;
  item_type: Item['item_type'];
  qty_on_hand: string;
  dealer_selling_price: string;
  cash_price: string;
  description: string;
  quantity: number;
  unit_price: number;
  discount_amount: number;
  tax_amount: number;
}

const createEmptyLine = (): LocalLine => ({
  item_id: 0,
  item_name: '',
  item_sku: '',
  item_type: 'INVENTORY',
  qty_on_hand: '0',
  dealer_selling_price: '0',
  cash_price: '0',
  description: '',
  quantity: 1,
  unit_price: 0,
  discount_amount: 0,
  tax_amount: 0,
});

const INITIAL_LINE_COUNT = 4;

const createInitialLines = (count = INITIAL_LINE_COUNT) =>
  Array.from({ length: count }, () => createEmptyLine());

const withTrailingEmptyLine = (lines: LocalLine[]): LocalLine[] => {
  if (lines.length === 0) return [createEmptyLine()];
  if (lines[lines.length - 1].item_id > 0) return [...lines, createEmptyLine()];
  return lines;
};

const mapPurchaseOrderLineToLocal = (line: PurchaseOrderLine): LocalLine => ({
  item_id: line.item_id,
  item_name: line.item_snapshot.name,
  item_sku: line.item_snapshot.sku,
  item_type: line.item?.item_type ?? 'INVENTORY',
  qty_on_hand: line.item?.qty_on_hand ?? '0',
  dealer_selling_price: line.item?.dealer_selling_price ?? '0',
  cash_price: line.item?.cash_price ?? '0',
  description: line.description ?? '',
  quantity: parseFloat(line.quantity),
  unit_price: parseFloat(line.unit_price),
  discount_amount: parseFloat(line.discount_amount),
  tax_amount: parseFloat(line.tax_amount),
});

const computeLineTotals = (line: LocalLine) => {
  const gross = round2(line.quantity * line.unit_price);
  const discount = Math.min(line.discount_amount, gross);
  const net = round2(gross - discount);
  const lineTotal = round2(net + line.tax_amount);
  return { gross, discount, net, lineTotal };
};

const computePurchaseOrderTotals = (lines: LocalLine[]) => {
  let subTotal = 0;
  let discountTotal = 0;
  let taxTotal = 0;

  for (const line of lines) {
    if (line.item_id === 0) continue;
    const { net, discount } = computeLineTotals(line);
    subTotal += net;
    discountTotal += discount;
    taxTotal += line.tax_amount;
  }

  return {
    subTotal: round2(subTotal),
    discountTotal: round2(discountTotal),
    taxTotal: round2(taxTotal),
    total: round2(subTotal + taxTotal),
  };
};

interface PurchaseOrderFormData {
  vendor_id: number;
  vendor_snapshot: CustomerSnapshot;
  po_date: string;
  expected_delivery_date: string;
  ship_to: string;
  status: PurchaseOrderStatus;
  memo: string;
  notes: string;
  lines: LocalLine[];
}

const lineInputClass =
  'w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-slate-500 focus:border-slate-500';

const autoResize = (el: HTMLTextAreaElement) => {
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight}px`;
};

const PurchaseOrdersManage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryEditId = Number(searchParams.get('id')) || 0;
  const isEditMode = queryEditId > 0;
  const canManage = canManagePurchaseOrders();
  const isReadOnly = !canManage;

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[] | undefined>>({});

  const [vendorOptions, setVendorOptions] = useState<SearchableSelectOption[]>([]);
  const [vendorSearchInput, setVendorSearchInput] = useState('');
  const [vendorSearchLoading, setVendorSearchLoading] = useState(false);
  const [selectedVendorOption, setSelectedVendorOption] =
    useState<SearchableSelectOption | null>(null);
  const selectedVendorOptionRef = useRef<SearchableSelectOption | null>(null);
  const vendorLookupRef = useRef<Map<number, Supplier>>(new Map());
  const lineRowRefs = useRef(new Map<number, HTMLTableRowElement>());

  const [showAddVendorModal, setShowAddVendorModal] = useState(false);
  const [showCreateItemModal, setShowCreateItemModal] = useState(false);
  const [createItemLineIndex, setCreateItemLineIndex] = useState<number | null>(null);
  const [taxTotalDraft, setTaxTotalDraft] = useState<string | null>(null);
  const [isCreatingVendor, setIsCreatingVendor] = useState(false);
  const [vendorFormError, setVendorFormError] = useState<string | null>(null);
  const [vendorFormData, setVendorFormData] = useState<CreateSupplierData>({
    display_name: '',
    email: '',
    phone: '',
    address: '',
    payment_terms: 'NET_30',
    active: true,
  });

  const [editingPurchaseOrder, setEditingPurchaseOrder] = useState<PurchaseOrder | null>(null);
  const [loadingVendorAccount, setLoadingVendorAccount] = useState(false);
  const [vendorOpenBalance, setVendorOpenBalance] = useState(0);
  const [vendorUnappliedTotal, setVendorUnappliedTotal] = useState(0);

  const today = todayLocalIso();
  const [formData, setFormData] = useState<PurchaseOrderFormData>({
    vendor_id: 0,
    vendor_snapshot: emptyCustomerSnapshot(),
    po_date: today,
    expected_delivery_date: '',
    ship_to: '',
    status: 'DRAFT',
    memo: '',
    notes: '',
    lines: [],
  });

  useEffect(() => {
    if (!canManage && !isEditMode) {
      navigate('/purchase-orders');
      return;
    }
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryEditId]);

  useEffect(() => {
    selectedVendorOptionRef.current = selectedVendorOption;
  }, [selectedVendorOption]);

  const loadVendorAccountSummary = useCallback(async (vendorId: number) => {
    setLoadingVendorAccount(true);
    try {
      const [payableBills, unappliedPaymentsList] = await Promise.all([
        getVendorPayableBills(vendorId),
        getBillPayments({ vendor_id: vendorId, unapplied: true }),
      ]);
      const openBalance = payableBills.reduce(
        (sum, bill) => sum + parseFloat(bill.balance),
        0
      );
      const unapplied = unappliedPaymentsList
        .filter((payment) => (payment.status ?? 'POSTED') !== 'VOIDED')
        .reduce((sum, payment) => sum + parseFloat(payment.unapplied_amount), 0);
      setVendorOpenBalance(openBalance);
      setVendorUnappliedTotal(unapplied);
    } catch {
      setVendorOpenBalance(0);
      setVendorUnappliedTotal(0);
    } finally {
      setLoadingVendorAccount(false);
    }
  }, []);

  useEffect(() => {
    if (formData.vendor_id > 0) {
      void loadVendorAccountSummary(formData.vendor_id);
    } else {
      setVendorOpenBalance(0);
      setVendorUnappliedTotal(0);
      setLoadingVendorAccount(false);
    }
  }, [formData.vendor_id, loadVendorAccountSummary]);

  useEffect(() => {
    const timer = setTimeout(async () => {
      setVendorSearchLoading(true);
      try {
        const result = await getSuppliers({
          search: vendorSearchInput.trim() || undefined,
          per_page: 20,
          page: 1,
        });
        for (const supplier of result.suppliers) {
          vendorLookupRef.current.set(supplier.id, supplier);
        }
        const options = result.suppliers.map(supplierToOption);
        const selected = selectedVendorOptionRef.current;
        setVendorOptions(
          selected && !options.some((opt) => String(opt.value) === String(selected.value))
            ? [selected, ...options]
            : options
        );
      } catch {
        // silently ignore
      } finally {
        setVendorSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [vendorSearchInput]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      vendorLookupRef.current.clear();
      setVendorSearchInput('');
      setVendorOpenBalance(0);
      setVendorUnappliedTotal(0);
      setLoadingVendorAccount(false);
      setEditingPurchaseOrder(null);

      if (isEditMode) {
        const po = await getPurchaseOrderById(queryEditId);
        setEditingPurchaseOrder(po);

        const vendorLabel = getPurchaseOrderVendorName(po);
        const selectedOption: SearchableSelectOption = {
          value: po.vendor_id,
          label: vendorLabel,
          description:
            [po.vendor_snapshot.email, po.vendor_snapshot.phone]
              .filter(Boolean)
              .join(' · ') || undefined,
        };
        setSelectedVendorOption(selectedOption);
        selectedVendorOptionRef.current = selectedOption;
        setVendorOptions([selectedOption]);

        const mappedLines = (po.lines ?? []).map(mapPurchaseOrderLineToLocal);

        setFormData({
          vendor_id: po.vendor_id,
          vendor_snapshot: { ...po.vendor_snapshot },
          po_date: po.po_date.slice(0, 10),
          expected_delivery_date: po.expected_delivery_date
            ? po.expected_delivery_date.slice(0, 10)
            : '',
          ship_to: po.ship_to ?? '',
          status: po.status,
          memo: po.memo ?? '',
          notes: po.notes ?? '',
          lines: isReadOnly ? mappedLines : withTrailingEmptyLine(mappedLines),
        });
      } else {
        setSelectedVendorOption(null);
        selectedVendorOptionRef.current = null;
        setVendorOptions([]);
        setFormData((prev) => ({
          ...prev,
          vendor_snapshot: emptyCustomerSnapshot(),
          lines: createInitialLines(),
        }));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleVendorChange = useCallback(async (vendorId: number) => {
    const id = Number(vendorId);
    if (!id) {
      setSelectedVendorOption(null);
      selectedVendorOptionRef.current = null;
      setVendorOpenBalance(0);
      setVendorUnappliedTotal(0);
      setLoadingVendorAccount(false);
      setFormData((prev) => ({
        ...prev,
        vendor_id: 0,
        vendor_snapshot: emptyCustomerSnapshot(),
      }));
      return;
    }

    let supplier = vendorLookupRef.current.get(id);
    if (!supplier) {
      try {
        supplier = await getSupplierById(id);
        vendorLookupRef.current.set(id, supplier);
      } catch {
        setFormData((prev) => ({ ...prev, vendor_id: id }));
        return;
      }
    }

    const option = supplierToOption(supplier);
    setSelectedVendorOption(option);
    selectedVendorOptionRef.current = option;
    setVendorOptions((prev) =>
      prev.some((opt) => String(opt.value) === String(id)) ? prev : [option, ...prev]
    );
    setFormData((prev) => ({
      ...prev,
      vendor_id: id,
      vendor_snapshot: supplierToSnapshot(supplier),
    }));
  }, []);

  const resetVendorFromSupplier = async () => {
    const id = formData.vendor_id;
    if (!id) return;

    let supplier = vendorLookupRef.current.get(id);
    if (!supplier) {
      try {
        supplier = await getSupplierById(id);
        vendorLookupRef.current.set(id, supplier);
      } catch {
        toast.error('Could not load vendor details.');
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      vendor_snapshot: supplierToSnapshot(supplier),
    }));
  };

  const updateVendorSnapshot = (patch: Partial<CustomerSnapshot>) => {
    setFormData((prev) => ({
      ...prev,
      vendor_snapshot: { ...prev.vendor_snapshot, ...patch },
    }));
  };

  const resetVendorForm = () => {
    setVendorFormData({
      display_name: '',
      email: '',
      phone: '',
      address: '',
      payment_terms: 'NET_30',
      active: true,
    });
    setVendorFormError(null);
  };

  const handleCreateVendor = async (e: FormEvent) => {
    e.preventDefault();
    setIsCreatingVendor(true);
    setVendorFormError(null);

    try {
      if (!vendorFormData.display_name?.trim()) {
        setVendorFormError('Display name is required.');
        return;
      }

      const newSupplier = await createSupplier({
        display_name: vendorFormData.display_name.trim(),
        email: vendorFormData.email?.trim() || null,
        phone: vendorFormData.phone?.trim() || null,
        address: vendorFormData.address?.trim() || null,
        payment_terms: vendorFormData.payment_terms,
        active: vendorFormData.active,
      });
      vendorLookupRef.current.set(newSupplier.id, newSupplier);
      setShowAddVendorModal(false);
      resetVendorForm();
      void handleVendorChange(newSupplier.id);
      toast.success(`Vendor "${newSupplier.display_name}" created and selected.`);
    } catch (err) {
      setVendorFormError(err instanceof Error ? err.message : 'Failed to create vendor');
    } finally {
      setIsCreatingVendor(false);
    }
  };

  const updateLine = (index: number, patch: Partial<LocalLine>) => {
    setFormData((prev) => ({
      ...prev,
      lines: prev.lines.map((line, i) => (i === index ? { ...line, ...patch } : line)),
    }));
  };

  const selectItemForLine = (index: number, item: Item | null) => {
    setFormData((prev) => {
      const lines = [...prev.lines];
      const current = lines[index] ?? createEmptyLine();

      if (!item) {
        lines[index] = createEmptyLine();
        const trimmed = lines.filter(
          (line, i) => line.item_id > 0 || i === lines.length - 1
        );
        return {
          ...prev,
          lines: trimmed.length > 0 ? withTrailingEmptyLine(trimmed) : [createEmptyLine()],
        };
      }

      lines[index] = {
        ...current,
        item_id: item.id,
        item_name: item.name,
        item_sku: item.sku ?? '',
        item_type: item.item_type,
        qty_on_hand: item.qty_on_hand,
        dealer_selling_price: item.dealer_selling_price,
        cash_price: item.cash_price,
        unit_price: parseFloat(item.standard_cost) || 0,
        description: item.description?.trim() ?? '',
      };

      return { ...prev, lines: withTrailingEmptyLine(lines) };
    });

    if (item) {
      const nextIndex = index + 1;
      scrollIntoViewAfterPaint(() => lineRowRefs.current.get(nextIndex));
    }
  };

  const openCreateItemModal = (lineIndex: number) => {
    setCreateItemLineIndex(lineIndex);
    setShowCreateItemModal(true);
  };

  const handleItemCreated = (item: Item, meta?: { imageError?: string }) => {
    if (meta?.imageError) {
      return;
    }

    if (createItemLineIndex !== null) {
      selectItemForLine(createItemLineIndex, item);
    }
    setShowCreateItemModal(false);
    setCreateItemLineIndex(null);
  };

  const applyPurchaseOrderTax = (totalTax: number) => {
    setFormData((prev) => {
      const activeIndices = prev.lines
        .map((line, index) => (line.item_id > 0 ? index : -1))
        .filter((index) => index >= 0);
      if (activeIndices.length === 0) return prev;

      const nets = activeIndices.map((index) => computeLineTotals(prev.lines[index]).net);
      const totalNet = nets.reduce((sum, net) => sum + net, 0);
      const lines = [...prev.lines];
      let allocated = 0;

      activeIndices.forEach((lineIndex, activeIndex) => {
        const isLast = activeIndex === activeIndices.length - 1;
        const share =
          totalNet > 0
            ? round2(totalTax * (nets[activeIndex] / totalNet))
            : round2(totalTax / activeIndices.length);
        const amount = isLast ? round2(totalTax - allocated) : share;
        allocated += amount;
        lines[lineIndex] = { ...lines[lineIndex], tax_amount: Math.max(0, amount) };
      });

      return { ...prev, lines };
    });
  };

  const handleRemoveLine = (index: number) => {
    setFormData((prev) => {
      const updated = prev.lines.filter((_, i) => i !== index);
      return {
        ...prev,
        lines: withTrailingEmptyLine(updated.filter((l) => l.item_id > 0)),
      };
    });
  };

  const validateForm = (): boolean => {
    if (!formData.vendor_id) {
      toast.error('Please select a vendor.');
      return false;
    }
    const snapshotError = validateCustomerSnapshot(formData.vendor_snapshot);
    if (snapshotError) {
      toast.error(snapshotError);
      return false;
    }
    if (!formData.po_date) {
      toast.error('PO date is required.');
      return false;
    }
    const activeLines = formData.lines.filter((l) => l.item_id > 0);
    if (activeLines.length === 0) {
      toast.error('Add at least one line item.');
      return false;
    }
    for (const line of activeLines) {
      if (line.quantity <= 0) {
        toast.error('All quantities must be greater than 0.');
        return false;
      }
      if (line.unit_price < 0) {
        toast.error('Unit price cannot be negative.');
        return false;
      }
      if (line.description.length > 1000) {
        toast.error(
          `Description is too long for ${line.item_name} (max 1000 characters).`
        );
        return false;
      }
    }
    return true;
  };

  const buildCreatePayload = (): CreatePurchaseOrderData => {
    const activeLines = formData.lines.filter((l) => l.item_id > 0);
    return {
      vendor_id: formData.vendor_id,
      vendor_snapshot: snapshotToPayload(formData.vendor_snapshot),
      po_date: formData.po_date,
      expected_delivery_date: formData.expected_delivery_date || null,
      ship_to: formData.ship_to || null,
      status: formData.status,
      memo: formData.memo || null,
      notes: formData.notes || null,
      lines: activeLines.map((line) => ({
        item_id: line.item_id,
        description: line.description.trim() || null,
        quantity: line.quantity,
        unit_price: line.unit_price,
        discount_amount: line.discount_amount || 0,
        tax_amount: line.tax_amount || 0,
      })),
    };
  };

  const buildUpdatePayload = (): UpdatePurchaseOrderData => {
    const activeLines = formData.lines.filter((l) => l.item_id > 0);
    return {
      vendor_id: formData.vendor_id,
      vendor_snapshot: snapshotToPayload(formData.vendor_snapshot),
      po_date: formData.po_date,
      expected_delivery_date: formData.expected_delivery_date || null,
      ship_to: formData.ship_to || null,
      status: formData.status,
      memo: formData.memo || null,
      notes: formData.notes || null,
      lines: activeLines.map((line) => ({
        item_id: line.item_id,
        description: line.description.trim() || null,
        quantity: line.quantity,
        unit_price: line.unit_price,
        discount_amount: line.discount_amount || 0,
        tax_amount: line.tax_amount || 0,
      })),
    };
  };

  const handleSave = async () => {
    blurActiveInputBeforeSave();
    setError(null);
    setFieldErrors({});
    if (!validateForm()) return;

    try {
      setSubmitting(true);

      if (isEditMode) {
        await updatePurchaseOrder(queryEditId, buildUpdatePayload());
        navigate('/purchase-orders', {
          state: { successMessage: 'Purchase order updated.' },
        });
      } else {
        await createPurchaseOrder(buildCreatePayload());
        navigate('/purchase-orders', {
          state: { successMessage: 'Purchase order created.' },
        });
      }
    } catch (err) {
      if (err instanceof ApiValidationError) {
        setFieldErrors(err.errors);
        setError(err.message);
      } else {
        toastApiError(err, 'Failed to save purchase order');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const fieldError = (field: string) => fieldErrors[field]?.[0];

  const estimatedTotals = useMemo(
    () => computePurchaseOrderTotals(formData.lines),
    [formData.lines]
  );

  const displayTotals = useMemo(() => {
    if (isReadOnly && editingPurchaseOrder) {
      return {
        subTotal: parseFloat(editingPurchaseOrder.sub_total) || 0,
        discountTotal: parseFloat(editingPurchaseOrder.discount_total) || 0,
        taxTotal: parseFloat(editingPurchaseOrder.tax_total) || 0,
        total: parseFloat(editingPurchaseOrder.total_amount) || 0,
      };
    }
    return estimatedTotals;
  }, [isReadOnly, editingPurchaseOrder, estimatedTotals]);

  const hasLineItems = formData.lines.some((l) => l.item_id > 0);

  if (loading) {
    return (
      <div className="flex h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-slate-800" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />

      <div className="flex-1 flex flex-col overflow-hidden min-h-0">
        <div className="flex-1 overflow-auto p-6 pb-28">
          <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
            <div>
              <Link
                to="/purchase-orders"
                className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-3"
              >
                <ArrowLeft className="h-4 w-4 mr-1.5" />
                Back to Purchase Orders
              </Link>
              <h1 className="text-2xl font-semibold text-gray-900">
                {isEditMode
                  ? isReadOnly
                    ? `View Purchase Order ${editingPurchaseOrder?.po_number ?? ''}`
                    : `Edit Purchase Order ${editingPurchaseOrder?.po_number ?? ''}`
                  : 'New Purchase Order'}
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                {isReadOnly
                  ? 'Read-only view — purchase orders do not affect ledger or inventory'
                  : 'Informational PO — no ledger, inventory, or bill effects'}
              </p>
              {isEditMode && editingPurchaseOrder && (
                <p className="text-sm text-gray-500 mt-2">
                  PO #{editingPurchaseOrder.po_number}
                  {' · '}
                  <span
                    className={`font-medium ${
                      editingPurchaseOrder.status === 'ACKNOWLEDGED'
                        ? 'text-green-700'
                        : editingPurchaseOrder.status === 'CANCELLED'
                          ? 'text-red-600'
                          : 'text-gray-700'
                    }`}
                  >
                    {formatLabel(editingPurchaseOrder.status)}
                  </span>
                </p>
              )}
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-400 shrink-0" />
                <p className="text-red-800 text-sm">{error}</p>
              </div>
            </div>
          )}

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 md:p-8 space-y-8">
            <section className="pb-8 border-b border-gray-100">
              <h3 className="text-base font-semibold text-gray-900 mb-5">PO details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                <div className="sm:col-span-2 xl:col-span-4">
                  <div className="grid grid-cols-1 gap-4">
                    <div className="min-w-0">
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Vendor <span className="text-red-500">*</span>
                      </label>
                      <div className="flex gap-2">
                        <SearchableSelect
                          className="flex-1 min-w-0"
                          value={formData.vendor_id}
                          onChange={(id) => void handleVendorChange(Number(id))}
                          placeholder="Select vendor"
                          searchPlaceholder="Search vendors..."
                          options={vendorOptions}
                          onSearchChange={setVendorSearchInput}
                          isLoading={vendorSearchLoading}
                          disabled={isReadOnly}
                        />
                        {!isReadOnly && (
                          <button
                            type="button"
                            onClick={() => setShowAddVendorModal(true)}
                            className="flex-shrink-0 inline-flex items-center px-3 py-2.5 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 bg-slate-50 hover:bg-slate-100"
                            title="Create new vendor"
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                      {fieldError('vendor_id') && (
                        <p className="mt-1 text-sm text-red-600">{fieldError('vendor_id')}</p>
                      )}
                    </div>

                    {formData.vendor_id > 0 && (
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div className="min-w-0">
                          <div className="flex items-center justify-between gap-3 mb-1.5">
                            <label className="block text-sm font-medium text-gray-700">
                              Vendor details{' '}
                              <span className="font-normal text-gray-500">(printed on PO)</span>
                            </label>
                            {!isReadOnly && (
                              <button
                                type="button"
                                onClick={() => void resetVendorFromSupplier()}
                                className="text-xs font-medium text-slate-600 hover:text-slate-900 shrink-0"
                              >
                                Reset to vendor
                              </button>
                            )}
                          </div>
                          <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-3 space-y-2">
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                              <div className="min-w-0">
                                <input
                                  type="text"
                                  maxLength={255}
                                  value={formData.vendor_snapshot.name}
                                  onChange={(e) => updateVendorSnapshot({ name: e.target.value })}
                                  placeholder="Name *"
                                  disabled={isReadOnly}
                                  className="w-full border border-gray-300 rounded-lg px-2.5 py-2 text-sm bg-white disabled:bg-gray-50 disabled:text-gray-700"
                                />
                                {fieldError('vendor_snapshot.name') && (
                                  <p className="mt-0.5 text-xs text-red-600">
                                    {fieldError('vendor_snapshot.name')}
                                  </p>
                                )}
                              </div>
                              <div className="min-w-0">
                                <input
                                  type="email"
                                  value={formData.vendor_snapshot.email ?? ''}
                                  onChange={(e) =>
                                    updateVendorSnapshot({ email: e.target.value || null })
                                  }
                                  placeholder="Email"
                                  disabled={isReadOnly}
                                  className="w-full border border-gray-300 rounded-lg px-2.5 py-2 text-sm bg-white disabled:bg-gray-50 disabled:text-gray-700"
                                />
                                {fieldError('vendor_snapshot.email') && (
                                  <p className="mt-0.5 text-xs text-red-600">
                                    {fieldError('vendor_snapshot.email')}
                                  </p>
                                )}
                              </div>
                              <div className="min-w-0">
                                <input
                                  type="text"
                                  maxLength={50}
                                  value={formData.vendor_snapshot.phone ?? ''}
                                  onChange={(e) =>
                                    updateVendorSnapshot({ phone: e.target.value || null })
                                  }
                                  placeholder="Phone"
                                  disabled={isReadOnly}
                                  className="w-full border border-gray-300 rounded-lg px-2.5 py-2 text-sm bg-white disabled:bg-gray-50 disabled:text-gray-700"
                                />
                                {fieldError('vendor_snapshot.phone') && (
                                  <p className="mt-0.5 text-xs text-red-600">
                                    {fieldError('vendor_snapshot.phone')}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div>
                              <textarea
                                rows={2}
                                maxLength={2000}
                                value={formData.vendor_snapshot.address ?? ''}
                                onChange={(e) =>
                                  updateVendorSnapshot({ address: e.target.value || null })
                                }
                                placeholder="Address"
                                disabled={isReadOnly}
                                className="w-full border border-gray-300 rounded-lg px-2.5 py-2 text-sm bg-white resize-y min-h-[2.5rem] disabled:bg-gray-50 disabled:text-gray-700"
                              />
                              {fieldError('vendor_snapshot.address') && (
                                <p className="mt-0.5 text-xs text-red-600">
                                  {fieldError('vendor_snapshot.address')}
                                </p>
                              )}
                            </div>
                            <p className="text-xs text-gray-500">
                              PO only — does not update the vendor record.
                            </p>
                          </div>
                        </div>

                        <div className="min-w-0">
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Vendor account
                          </label>
                          <div className="rounded-lg border border-gray-200 bg-gray-50/50 px-3 py-2.5 space-y-1.5 text-sm">
                            <div className="flex items-center justify-between gap-3">
                              <span className="text-gray-500">Open balance</span>
                              <span className="font-semibold text-red-600 tabular-nums whitespace-nowrap">
                                {loadingVendorAccount ? '—' : formatCurrency(vendorOpenBalance)}
                              </span>
                            </div>
                            <div className="flex items-center justify-between gap-3">
                              <span className="text-gray-500">Unapplied prepayment</span>
                              <span className="font-semibold text-amber-700 tabular-nums whitespace-nowrap">
                                {loadingVendorAccount ? '—' : formatCurrency(vendorUnappliedTotal)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    PO date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.po_date}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, po_date: e.target.value }))
                    }
                    disabled={isReadOnly}
                    className="w-full border border-gray-300 rounded-lg px-3.5 py-2.5 text-sm disabled:bg-gray-50 disabled:text-gray-700"
                  />
                  {fieldError('po_date') && (
                    <p className="mt-1 text-sm text-red-600">{fieldError('po_date')}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Expected delivery
                  </label>
                  <input
                    type="date"
                    value={formData.expected_delivery_date}
                    min={formData.po_date}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        expected_delivery_date: e.target.value,
                      }))
                    }
                    disabled={isReadOnly}
                    className="w-full border border-gray-300 rounded-lg px-3.5 py-2.5 text-sm disabled:bg-gray-50 disabled:text-gray-700"
                  />
                  {fieldError('expected_delivery_date') && (
                    <p className="mt-1 text-sm text-red-600">
                      {fieldError('expected_delivery_date')}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        status: e.target.value as PurchaseOrderStatus,
                      }))
                    }
                    disabled={isReadOnly}
                    className="w-full border border-gray-300 rounded-lg px-3.5 py-2.5 text-sm bg-white disabled:bg-gray-50 disabled:text-gray-700"
                  >
                    {PURCHASE_ORDER_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {formatLabel(s)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="sm:col-span-2 xl:col-span-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Ship to
                  </label>
                  <input
                    type="text"
                    value={formData.ship_to}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, ship_to: e.target.value }))
                    }
                    placeholder="Delivery address or warehouse note"
                    disabled={isReadOnly}
                    className="w-full border border-gray-300 rounded-lg px-3.5 py-2.5 text-sm disabled:bg-gray-50 disabled:text-gray-700"
                  />
                </div>
              </div>
            </section>

            <section className="pb-8 border-b border-gray-100">
              <h3 className="text-base font-semibold text-gray-900 mb-5">Line items</h3>

              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="min-w-full divide-y divide-gray-200 text-sm table-fixed">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="w-5 px-0.5 py-2.5 text-center text-xs font-medium text-gray-500 uppercase">
                        #
                      </th>
                      <th className="w-[18%] px-2 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">
                        Product / service
                      </th>
                      <th className="w-[28%] px-2 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">
                        Description
                      </th>
                      <th className="w-[9%] px-2 py-2.5 text-right text-xs font-medium text-gray-500 uppercase">
                        Qty
                      </th>
                      <th className="w-[11%] px-2 py-2.5 text-right text-xs font-medium text-gray-500 uppercase">
                        Rate
                      </th>
                      <th className="w-[9%] px-2 py-2.5 text-right text-xs font-medium text-gray-500 uppercase">
                        Discount
                      </th>
                      <th className="w-[11%] px-2 py-2.5 text-right text-xs font-medium text-gray-500 uppercase">
                        Amount
                      </th>
                      <th className="w-8 px-1 py-2.5" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {formData.lines.map((line, index) => {
                      const isEmptyRow = line.item_id === 0;
                      const { net } = computeLineTotals(line);

                      return (
                        <tr
                          key={`line-${index}`}
                          ref={(el) => {
                            if (el) lineRowRefs.current.set(index, el);
                            else lineRowRefs.current.delete(index);
                          }}
                          className={`align-top ${isEmptyRow ? 'bg-white' : 'bg-slate-50/40'}`}
                        >
                          <td className="w-5 px-0.5 py-2 text-center text-gray-500 tabular-nums">
                            {index + 1}
                          </td>
                          <td className="px-2 py-2">
                            <div className="flex gap-1">
                              <AsyncItemSelect
                                className="flex-1 min-w-0"
                                value={line.item_id}
                                selectedLabel={line.item_name || undefined}
                                onSelect={(item) => selectItemForLine(index, item)}
                                onClear={() => selectItemForLine(index, null)}
                                placeholder="Select item"
                                searchPlaceholder="Search by name or SKU..."
                                disabled={isReadOnly}
                              />
                              {!isReadOnly && (
                                <button
                                  type="button"
                                  onClick={() => openCreateItemModal(index)}
                                  className="flex-shrink-0 inline-flex items-center px-2 py-2.5 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 bg-slate-50 hover:bg-slate-100"
                                  title="Create new item"
                                >
                                  <Plus className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                            {line.item_id > 0 && (
                              <p className="mt-1 text-xs text-gray-400 font-mono truncate">
                                {line.item_sku}
                              </p>
                            )}
                          </td>
                          <td className="px-2 py-2">
                            <textarea
                              ref={(el) => {
                                if (el) autoResize(el);
                              }}
                              rows={1}
                              maxLength={1000}
                              value={line.description}
                              onChange={(e) => {
                                updateLine(index, { description: e.target.value });
                                autoResize(e.target);
                              }}
                              placeholder="Description"
                              disabled={isReadOnly}
                              className={`${lineInputClass} min-h-[2.25rem] resize-none overflow-hidden disabled:bg-gray-50 disabled:text-gray-700`}
                            />
                          </td>
                          <td className="px-2 py-2">
                            <DecimalInput
                              value={line.quantity}
                              onCommit={(quantity) => updateLine(index, { quantity })}
                              min={0.0001}
                              disabled={isEmptyRow || isReadOnly}
                              className={`${lineInputClass} text-right disabled:bg-gray-50 disabled:text-gray-400`}
                            />
                            {line.item_id > 0 && (
                              <div className="mt-1 space-y-0.5 text-xs text-right">
                                {line.item_type === 'INVENTORY' && (
                                  <p className={`truncate ${qtyOnHandClassName(line.qty_on_hand)}`}>
                                    On hand: {formatPreviewQuantity(line.qty_on_hand)}
                                  </p>
                                )}
                                <p className="truncate">
                                  Dealer: {formatCurrency(line.dealer_selling_price)}
                                </p>
                                <p className="truncate">
                                  Cash: {formatCurrency(line.cash_price)}
                                </p>
                              </div>
                            )}
                          </td>
                          <td className="px-2 py-2">
                            <DecimalInput
                              value={line.unit_price}
                              onCommit={(unit_price) => updateLine(index, { unit_price })}
                              min={0}
                              decimals={4}
                              formatDisplay={(n) => String(displayRate(n))}
                              disabled={isEmptyRow || isReadOnly}
                              className={`${lineInputClass} text-right disabled:bg-gray-50 disabled:text-gray-400`}
                            />
                          </td>
                          <td className="px-2 py-2">
                            <DecimalInput
                              value={line.discount_amount}
                              onCommit={(discount_amount) =>
                                updateLine(index, { discount_amount })
                              }
                              min={0}
                              disabled={isEmptyRow || isReadOnly}
                              className={`${lineInputClass} text-right disabled:bg-gray-50 disabled:text-gray-400`}
                            />
                          </td>
                          <td className="px-2 py-2 text-right text-sm font-medium text-gray-900 tabular-nums">
                            {isEmptyRow ? (
                              <span className="text-gray-300">—</span>
                            ) : (
                              formatCurrency(net)
                            )}
                          </td>
                          <td className="px-1 py-2">
                            {!isEmptyRow && !isReadOnly && (
                              <button
                                type="button"
                                onClick={() => handleRemoveLine(index)}
                                className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg"
                                title="Remove line"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
                <div className="space-y-6">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Memo</label>
                      <input
                        type="text"
                        value={formData.memo}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, memo: e.target.value }))
                        }
                        placeholder="Printed on PO"
                        disabled={isReadOnly}
                        className="w-full border border-gray-300 rounded-lg px-3.5 py-2.5 text-sm disabled:bg-gray-50 disabled:text-gray-700"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Internal Notes
                      </label>
                      <textarea
                        value={formData.notes}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, notes: e.target.value }))
                        }
                        rows={3}
                        placeholder="Internal notes only"
                        disabled={isReadOnly}
                        className="w-full border border-gray-300 rounded-lg px-3.5 py-2.5 text-sm disabled:bg-gray-50 disabled:text-gray-700"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-slate-50 rounded-xl border border-slate-200 p-5">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-4">
                    Estimated breakdown
                  </p>
                  {hasLineItems ? (
                    <div className="space-y-2.5 text-sm">
                      {displayTotals.discountTotal > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Discount</span>
                          <span className="font-medium text-red-600">
                            −{formatCurrency(displayTotals.discountTotal)}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-gray-600">Subtotal</span>
                        <span className="font-medium text-gray-900">
                          {formatCurrency(displayTotals.subTotal)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center gap-2">
                        <span className="text-gray-600">Tax</span>
                        {isReadOnly ? (
                          <span className="font-medium text-gray-900">
                            {formatCurrency(displayTotals.taxTotal)}
                          </span>
                        ) : (
                          <input
                            type="number"
                            min={0}
                            step="any"
                            placeholder="0.00"
                            value={
                              taxTotalDraft ??
                              (displayTotals.taxTotal > 0 ? String(displayTotals.taxTotal) : '')
                            }
                            onFocus={() => {
                              setTaxTotalDraft(
                                displayTotals.taxTotal > 0 ? String(displayTotals.taxTotal) : ''
                              );
                            }}
                            onChange={(e) => setTaxTotalDraft(e.target.value)}
                            onBlur={() => {
                              const val = parseFloat(taxTotalDraft ?? '');
                              setTaxTotalDraft(null);
                              if (!isNaN(val) && val >= 0) applyPurchaseOrderTax(val);
                            }}
                            className="w-28 border border-gray-300 rounded px-2 py-1 text-right text-sm focus:outline-none focus:ring-1 focus:ring-slate-500 focus:border-slate-500"
                          />
                        )}
                      </div>
                      <div className="flex justify-between pt-3 mt-1 border-t border-slate-200">
                        <span className="text-base font-semibold text-gray-900">Total</span>
                        <span className="text-base font-bold text-slate-800">
                          {formatCurrency(displayTotals.total)}
                        </span>
                      </div>
                      {!isReadOnly && (
                        <p className="text-xs text-gray-400 pt-1">
                          Estimated — server recalculates on save
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">Add line items to see totals</p>
                  )}
                </div>
              </div>
            </section>
          </div>
        </div>

        <div className="flex-shrink-0 border-t border-gray-200 bg-white px-6 py-4 shadow-[0_-4px_12px_rgba(0,0,0,0.06)]">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="text-sm text-gray-600">
              {hasLineItems ? (
                <>
                  <span className="text-gray-500 mr-3">
                    Subtotal {formatCurrency(displayTotals.subTotal)}
                  </span>
                  <span className="text-gray-500 mr-3">
                    Tax {formatCurrency(displayTotals.taxTotal)}
                  </span>
                  <span className="text-lg font-bold text-slate-800">
                    {formatCurrency(displayTotals.total)}
                  </span>
                </>
              ) : (
                <span className="text-gray-400">No line items</span>
              )}
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={() => navigate('/purchase-orders')}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700"
              >
                {isReadOnly ? 'Back' : 'Cancel'}
              </button>
              {!isReadOnly && (
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={submitting || !hasLineItems}
                  className={`${btnPrimary} px-6 py-2.5 text-sm font-semibold disabled:opacity-50`}
                >
                  {submitting
                    ? 'Saving...'
                    : isEditMode
                      ? 'Save Changes'
                      : 'Save Purchase Order'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {showCreateItemModal && (
        <ItemFormModal
          subtitle="Adds the item to your catalog and selects it on this line."
          onClose={() => {
            setShowCreateItemModal(false);
            setCreateItemLineIndex(null);
          }}
          onSaved={handleItemCreated}
        />
      )}

      {showAddVendorModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
            <div className="px-6 py-4 border-b flex justify-between items-center">
              <div className="flex items-center gap-3">
                <Truck className="h-6 w-6 text-slate-700" />
                <h3 className="text-lg font-semibold">Add Vendor</h3>
              </div>
              <button type="button" onClick={() => setShowAddVendorModal(false)}>
                <X className="h-5 w-5 text-gray-400" />
              </button>
            </div>
            <form onSubmit={handleCreateVendor}>
              <div className="p-6 space-y-4">
                {vendorFormError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
                    {vendorFormError}
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium mb-1">Display Name *</label>
                  <input
                    type="text"
                    required
                    value={vendorFormData.display_name}
                    onChange={(e) =>
                      setVendorFormData((prev) => ({ ...prev, display_name: e.target.value }))
                    }
                    className="w-full border border-gray-300 rounded-lg px-3.5 py-2.5 text-sm"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Email</label>
                    <input
                      type="email"
                      value={vendorFormData.email ?? ''}
                      onChange={(e) =>
                        setVendorFormData((prev) => ({ ...prev, email: e.target.value }))
                      }
                      className="w-full border border-gray-300 rounded-lg px-3.5 py-2.5 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Phone</label>
                    <input
                      type="tel"
                      value={vendorFormData.phone ?? ''}
                      onChange={(e) =>
                        setVendorFormData((prev) => ({ ...prev, phone: e.target.value }))
                      }
                      className="w-full border border-gray-300 rounded-lg px-3.5 py-2.5 text-sm"
                    />
                  </div>
                </div>
              </div>
              <div className="px-6 py-4 bg-gray-50 border-t flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowAddVendorModal(false)}
                  className="px-4 py-2 border rounded-lg text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isCreatingVendor}
                  className="px-4 py-2 rounded-lg text-sm text-white bg-slate-800 disabled:opacity-60"
                >
                  {isCreatingVendor ? 'Creating...' : 'Create Vendor'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseOrdersManage;
